import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux'; 
import { useNavigate } from 'react-router-dom';  
import { RootState } from '../../../store/store'; 
import { Grid, Container, Typography,Box } from '@mui/material';  
import { useParams } from "react-router-dom";
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import VisibilityIcon from '@mui/icons-material/Visibility';
import themeEn from '../../../themes/index.en';
import themeAr from '../../../themes/index.ar';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import { SignInFormStyles } from '../../../assets/css/signInForm.styles';  
import { useAppDispatch } from '../../../hooks/useAppDispatch';
import CustomLabeledTextField from '../../../components/customTextField/customLabeledTextField';
import CustomButton from '../../../components/customButton/customButton';
import { useTranslation } from 'react-i18next';
import { changePassword, forgotPassword } from '../../../reducers/authReducer';
import { Lowercase, Numbercase, passwordCharactersMinimum, SpecialChar, Uppercase } from '../../../constants/registerConstants';
import { sendActionStatus } from '../../../reducers/externalApiReducer';

const ChangePasswordForm: React.FC = () => {
    const { t }: { t: (key: string) => string } = useTranslation();
    const { i18n } = useTranslation();
    const currentLanguage = i18n.language;
    const isRTL = currentLanguage === 'ar'; 
    const [password, setPassword] = useState('');  
    const [confirmPassword, setConfirmPassword] = useState('');
    const [isVisible, setIsVisible] = useState(false); 
    const [touched, setTouched] = useState({ password: false, confirmPassword:false });
    const authState = useSelector((state: RootState) => state.auth);
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const { id } = useParams();
 
useEffect(() => {
    if(!id){
        navigate('/ForgotPassword');
    }
}, [id]);

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (password.length < passwordCharactersMinimum || !Uppercase.test(password) || !Lowercase.test(password) || !Numbercase.test(password) || !SpecialChar.test(password)) { 
        dispatch(sendActionStatus({token:'', status:false, action:'changePassword',message:`${t('passwordMandatoryValidation')}` ,redirection:'' }));  
        return;
    }
    if (confirmPassword !== password) { 
        dispatch(sendActionStatus({token:'', status:false, action:'changePassword',message:`${t('doesNotMatchValidation')}` ,redirection:'' }));  
        return;
    }
    dispatch(changePassword({ id:id?.toString() || "", password: password })).unwrap()  
    .then((response) => { 
        const { message, success, messageCode  } = response.data;   
       if(success){ 
            dispatch(sendActionStatus({token:'', status:true, action:'changePassword',message:`${t(messageCode || "")}`,redirection:'/SignIn' }));  
        }
        else{ 
            dispatch(sendActionStatus({token:'', status:false, action:'changePassword',message:`${t(messageCode || "")}`,redirection:'' }));  
        }  
    }).catch((error) => { 
        console.error(error);
    }); 
  };

  const handlePasswordBlur = () => { 
      if (password.length < passwordCharactersMinimum || !Uppercase.test(password) || !Lowercase.test(password) || !Numbercase.test(password) || !SpecialChar.test(password)
    ) {
          setTouched((prevData) => ({
              ...prevData,
              password: true,
          })) 
      } 
    };
  
  const handleConfirmPasswordBlur = () => { 
      if (confirmPassword.trim() === "" || confirmPassword !== password) {
          setTouched((prevData) => ({
              ...prevData,
              confirmPassword: true,
          })) 
      } 
    }; 

  return (
    <ThemeProvider theme={isRTL ? themeAr : themeEn}>
    <Container component="main" maxWidth="sm"  sx={SignInFormStyles.container}>
        <div> 
            <Typography variant="h5" component="h2" align="center" sx={{ marginBottom:"25px" }}>
               {t('resetPassword')}
            </Typography>
        </div>  
        <Box component="form" onSubmit={handleSubmit} noValidate style={SignInFormStyles.form}> 
            <CustomLabeledTextField  
                label={t('newPassword')}
                name="password"
                isRTL={isRTL}
                type={isVisible ? "text" :"password"}
                value={password}
                icon={isVisible ? <VisibilityOffIcon /> : <VisibilityIcon />}
                onClickOnIcon={() => setIsVisible(!isVisible)}  
                onChange={(e) =>
                setPassword(e.target.value)
                }
                required={true}
                onBlur={handlePasswordBlur}
                error={touched.password && (
                password.trim() === "" || password.length < passwordCharactersMinimum || !Uppercase.test(password) || !Lowercase.test(password) || !Numbercase.test(password) || !SpecialChar.test(password)  
                )}
                 helperText={
                    (touched.password && (password.trim() === "" || password.length < passwordCharactersMinimum))
                    ? `${t('passwordMandatoryValidation')}`
                    : (touched.password && !Uppercase.test(password))  ? `${t('upperCaseValidation')}`
                    : (touched.password && !Lowercase.test(password))  ? `${t('lowerCaseValidation')}` 
                    : (touched.password && !Numbercase.test(password))  ? `${t('numericValidation')}`
                    : (touched.password && !SpecialChar.test(password))  ? `${t('specialCharsValidation')}`
                    : ""
                }
            />   
            <CustomLabeledTextField  
                label={t('confirmPassword')}
                name="confirmPassword" 
                type="password" 
                isRTL={isRTL}
                value={confirmPassword}
                onChange={(e) =>
                setConfirmPassword(e.target.value) 
                }
                required={true} 
                onBlur={handleConfirmPasswordBlur}
                error={touched.confirmPassword && (confirmPassword.trim() === "" || confirmPassword !== password)} 
                helperText={
                    touched.confirmPassword && confirmPassword.trim() === "" ? `${t('confirmPasswordValidation')}`: 
                    touched.confirmPassword && confirmPassword !== password ? `${t('doesNotMatchValidation')}`  : ""}
            /> 
            <CustomButton style={SignInFormStyles.button}>
                {t('changePassword')}
            </CustomButton> 
        </Box> 
    </Container>
    </ThemeProvider>
  );
};

export default ChangePasswordForm;
